import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Globe, Wand2, Settings, Trash2 } from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { parseAdminStaffSession } from '../utils/adminSession';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';
import { PageSettingsModal } from '../components/admin/PageSettingsModal';
import { AgentAutogenerateModal } from '../components/admin/AgentAutogenerateModal';
import type { AgentTemplate } from '../types/agent';
import type { FormSection } from '../types/formBuilder';
import { validateFormConfig } from '../utils/formBuilderHelpers';
import { flattenOnboardingFields, getOnboardingAnswerKey } from '../utils/onboardingFormSchema';
const DEFAULT_NEW_AGENT_PRICING = {
  free: true,
  day: 10,
  month: 100,
  year: 1000,
} as const;

export const AgentsPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageModalOpen, setPageModalOpen] = useState(false);
  const [pageModalTemplate, setPageModalTemplate] = useState<AgentTemplate | null>(null);
  const [showAutogenerate, setShowAutogenerate] = useState(false);
  /** IDs with ≥1 `user_agent_instances` row (not deletable). `null` = lookup failed — hide delete for safety. */
  const [templateIdsWithInstances, setTemplateIdsWithInstances] = useState<Set<string> | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const parsedAccess = parseAdminStaffSession();
  const isSuperAdmin = parsedAccess?.role === 'superadmin';
  /** Autogenerate needs staff admin session + Supabase Auth JWT. */
  const autogenReady = Boolean(parsedAccess?.hy_user_id?.trim());

  useEffect(() => {
    const staff = parseAdminStaffSession();
    if (!staff) {
      navigate('/admin');
    }
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    const [tplRes, instRes] = await Promise.all([
      supabase.from('agent_templates').select('*').order('updated_at', { ascending: false }),
      supabase.from('user_agent_instances').select('agent_template_id'),
    ]);
    if (!tplRes.error && tplRes.data) setTemplates(tplRes.data as AgentTemplate[]);
    if (!instRes.error && instRes.data) {
      setTemplateIdsWithInstances(new Set(instRes.data.map((r) => r.agent_template_id)));
    } else {
      setTemplateIdsWithInstances(null);
    }
    setLoading(false);
  };

  const handleDeleteTemplate = async (t: AgentTemplate) => {
    if (!templateIdsWithInstances || templateIdsWithInstances.has(t.id)) return;
    const label = t.name.trim() || t.slug || t.id;
    if (!window.confirm(`Delete agent template "${label}"? This cannot be undone.`)) return;
    setDeletingId(t.id);
    const { error } = await supabase.from('agent_templates').delete().eq('id', t.id);
    setDeletingId(null);
    if (error) {
      alert(error.message || 'Could not delete agent');
      return;
    }
    void load();
  };

  useEffect(() => {
    load();
  }, []);

  const handleAutogenerateApply = async (payload: {
    name: string;
    publicDescription: string;
    systemInstructions: string;
    guardrails: string;
    sections: FormSection[];
  }) => {
    const nameTrim = payload.name.trim();
    if (!nameTrim) {
      throw new Error('Generated name is empty');
    }
    const formValidation = validateFormConfig(payload.sections);
    if (!formValidation.isValid) {
      throw new Error(formValidation.errors[0] || 'Onboarding form validation failed');
    }
    const allFields = flattenOnboardingFields(payload.sections);
    const seenKeys = new Set<string>();
    for (const field of allFields) {
      const key = getOnboardingAnswerKey(field);
      if (seenKeys.has(key)) {
        throw new Error(`Duplicate field key "${key}" — use unique variable names or field IDs.`);
      }
      seenKeys.add(key);
    }

    const staff = parseAdminStaffSession();

    const onboarding_form_schema = { sections: payload.sections };
    const row = {
      name: nameTrim,
      public_description: payload.publicDescription.trim() || null,
      cover_media_url: null,
      cover_media_type: null,
      system_instructions: payload.systemInstructions,
      guardrails: payload.guardrails.trim(),
      onboarding_form_enabled: true,
      tags: [] as string[],
      onboarding_form_schema,
      pricing: { ...DEFAULT_NEW_AGENT_PRICING },
      knowledge_base_enabled: false,
      knowledge_base_text: null,
      file_uploads_enabled: false,
      tool_serper: false,
      tool_tavily: false,
      tool_fal: false,
      voice_enabled: false,
      openai_model: 'openrouter/auto',
      is_active: true,
      hy_user_id: staff?.hy_user_id || null,
      updated_at: new Date().toISOString(),
    };

    const { data: created, error } = await supabase.from('agent_templates').insert(row).select('id').single();
    if (error) {
      throw new Error(error.message);
    }
    if (!created?.id) {
      throw new Error('Could not create agent');
    }
    void load();
    navigate(`/admin/agents/edit/${created.id}?openPageSettings=1`);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar title="AI Agents" subtitle="Manage conversational agent templates">
        <div className="flex w-full sm:w-auto flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowAutogenerate(true)}
            disabled={!autogenReady}
            title={
              autogenReady
                ? 'Generate and save a conversational agent (human-like tone) from an app description'
                : 'Sign in as admin and open with ?id=… in the URL (or use dev mock)'
            }
            className="flex items-center gap-2 px-4 py-2 bg-accent text-surface rounded-lg font-medium hover:bg-accent/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Wand2 className="w-4 h-4 shrink-0" aria-hidden />
            <span className="hidden sm:inline">Autogenerate</span>
          </button>
          {isSuperAdmin ? (
            <button
              type="button"
              onClick={() => navigate('/admin/agents/autogen-settings')}
              title="Autogenerate prompt & defaults (superadmin)"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-subtle bg-elevated text-th-secondary hover:bg-overlay hover:text-th-primary transition-colors"
              aria-label="Autogenerate settings"
            >
              <Settings className="w-5 h-5" aria-hidden />
            </button>
          ) : null}
          <button
            type="button"
            className="button-primary flex items-center gap-2"
            onClick={() => navigate('/admin/agents/create')}
          >
            <Plus className="w-4 h-4" />
            New agent
          </button>
        </div>
      </AdminPageToolbar>

      <main className="min-h-0 flex-1 overflow-y-auto max-w-6xl mx-auto px-4 py-6 w-full">
        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-th-secondary text-center py-12">No agents yet. Create one to get started.</p>
        ) : (
          <div className="grid gap-4">
            {templates.map((t) => (
              <div key={t.id} className="card p-4 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="font-semibold text-th-primary">{t.name}</h2>
                  <p className="text-sm text-th-secondary">{t.slug || t.id}</p>
                  <p className="text-xs text-th-tertiary mt-1">{t.is_active ? 'Active' : 'Inactive'}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="button-secondary text-sm"
                    onClick={() => navigate(`/admin/agents/edit/${t.id}`)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="button-secondary text-sm flex items-center gap-1"
                    onClick={() => {
                      setPageModalTemplate(t);
                      setPageModalOpen(true);
                    }}
                  >
                    <Globe className="w-4 h-4" />
                    Public page
                  </button>
                  {templateIdsWithInstances !== null && !templateIdsWithInstances.has(t.id) ? (
                    <button
                      type="button"
                      disabled={deletingId === t.id}
                      title="Remove this template (no user has created an agent instance yet)"
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-status-error/35 text-status-error hover:bg-status-error/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      onClick={() => void handleDeleteTemplate(t)}
                    >
                      {deletingId === t.id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
                      )}
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {pageModalTemplate && (
        <PageSettingsModal
          isOpen={pageModalOpen}
          onClose={() => {
            setPageModalOpen(false);
            setPageModalTemplate(null);
          }}
          modelconfigId=""
          modelName={pageModalTemplate.name}
          variant="agent"
          agentTemplateId={pageModalTemplate.id}
        />
      )}

      <AgentAutogenerateModal
        isOpen={showAutogenerate}
        onClose={() => setShowAutogenerate(false)}
        onApply={handleAutogenerateApply}
      />
    </div>
  );
};
